import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Child } from "../../api/child";
import { useGlobalStore } from "../../api/globalStores";
import { TeacherFeedback } from "../../api/teacherFeedback";
import ButtonPrimary from "../../Common/Buttons/ButtonPrimary";
import BaseCard from "../../Common/Cards/BaseCard";
import Checkbox from "../../Common/Forms/Checkbox";
import IconFeedback from "../../Common/Icons/IconFeedback";
import IconStar from "../../Common/Icons/IconStar";
import Modal from "../../Common/Utility/Modal";
import { getEventDay, getEventMonth } from "../../util/getEventMonth";

type FromTriggerLocation = "TeacherLesson" | "StudentFeedbackPage";

interface TeacherFeedbackFormProps {
  formTriggerLocation: FromTriggerLocation;
  onSubmit?: () => void;
}

export default function TeacherFeedbackForm(props: TeacherFeedbackFormProps) {
  const globalStores = useGlobalStore();

  const [teacherFeedback, setTeacherFeedback] = useState<TeacherFeedback>();
  const [allStudents, setAllStudents] = useState<Child[]>([]);
  const [additionalComments, setAdditionalComments] = useState("");
  const [engagementImprovementPlan, setEngagementImprovementPlan] =
    useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      const feedback =
        await globalStores.curTalkingParty.createTeacherFeedback();
      setTeacherFeedback(feedback);
    };

    run();
  }, []);

  useEffect(() => {
    globalStores.curTalkingParty.pullDetails();
    setAllStudents(globalStores.curClass.childs);
  }, []);

  useEffect(() => {
    if (!teacherFeedback?.teacherFeedback.engagementImprovementPlan) return;
    setEngagementImprovementPlan(
      teacherFeedback.teacherFeedback.engagementImprovementPlan
    );
  }, [teacherFeedback?.teacherFeedback.engagementImprovementPlan]);

  useEffect(() => {
    if (!teacherFeedback?.teacherFeedback.additionalComments) return;
    setAdditionalComments(teacherFeedback.teacherFeedback.additionalComments);
  }, [teacherFeedback?.teacherFeedback.additionalComments]);

  const submitFeedback = async () => {
    props.onSubmit && props.onSubmit();
    switch (props.formTriggerLocation) {
      case "StudentFeedbackPage":
        navigate("/teacher/student_feedback");
        break;
      case "TeacherLesson":
        navigate("/teacher/calendar");
        await globalStores.curTalkingParty.leaveRoom();
        break;
    }
    console.log("this ran!");
  };

  const startTimeNumber =
    globalStores.curTalkingParty.talkingParty.startTime ?? 0;
  const startTime = new Date(startTimeNumber);

  const eventDay = getEventDay(startTime);
  const eventMonth = getEventMonth(startTime);
  return (
    <div className="h-[520px] w-[520px] overflow-hidden bg-brij-White rounded-[20px] p-3">
      <div className="bg-brij-White inline-flex flex-col items-start justify-start gap-6 p-5 h-full overflow-y-scroll">
        <div className="flex flex-col items-start justify-start gap-2">
          <p className="text-heading3 w-full font-medium">
            Class {globalStores.curClass.class.id} : Level{" "}
            {globalStores.curClass.class.level}
          </p>
          <div className="h-6 w-full">
            <p className="text-brij-Black inline leading-[1.5]">
              Module:{" "}
              <span className="font-normal">How to ask for directions</span>
            </p>
          </div>
          <p className="w-full leading-[1.5] text-black">
            {eventDay}, {eventMonth} {startTime?.getDate()} /{" "}
            {startTime.getHours() ?? 0}:00 - {(startTime.getHours() ?? 0) + 1}
            :00
          </p>
        </div>
        <p className="text-xlarge w-full font-medium">
          Child Learning Feedback
        </p>
        <p>Please rate how engaged the students were:</p>
        <div className="inline-flex flex-col gap-3">
          {allStudents.map((student) => {
            const childFeedbackObj = teacherFeedback?.childTeacherFeedback.find(
              (singleFeedback) => {
                return singleFeedback.child === student.childAccount.id;
              }
            );
            const initialRating = childFeedbackObj?.engagementLevel;
            const initialEngagementPlan =
              childFeedbackObj?.engagementIncreasePlan;
            return (
              <StarRatingForm
                key={student.childAccount.id}
                name={student.userDetail.firstName ?? ""}
                initialRating={initialRating ?? 3}
                initialFeedback={initialEngagementPlan ?? ""}
                onChangeFeedback={(newEngagementPlan) => {
                  if (!student.childAccount.id) return;
                  if (!teacherFeedback) return;

                  teacherFeedback.setStudentEngagementPlan(
                    student.childAccount.id,
                    newEngagementPlan
                  );
                }}
                onChangeRating={(newRating) => {
                  if (!student.childAccount.id) return;
                  if (!teacherFeedback) return;

                  toast.promise(
                    teacherFeedback.rateStudentEngagement(
                      student.childAccount.id,
                      newRating
                    ),
                    {
                      success: "Student Engagement Updated",
                      loading: "Updating Student Engagement",
                      error: (e) => e.message,
                    }
                  );
                }}
              />
            );
          })}
          <p className="text-xlarge w-full font-medium">
            Specific Student Feedback
          </p>
          <p>
            For specific students, how would you engage him/her in the next
            lesson?
          </p>
          {teacherFeedback && (
            <SpecificStudentFeedback
              teacherFeedback={teacherFeedback}
              allStudents={allStudents}
            />
          )}

          {/* <div className="flex justify-end w-full -mt-2">
            <button
              className="inline-flex hover:bg-brij-Grey hover:bg-opacity-20 px-3 py-2 rounded-xl"
              onClick={addSpecificFeedback}
            >
              <div className="w=[24px] h-[24px] stroke-brij-Black">
                <IconPlus />
              </div>
              Add Another Student
            </button>
          </div> */}
          <p>Do you have any additional comments?</p>
          <div className="w-full">
            <textarea
              className="rounded-xl w-full"
              rows={3}
              placeholder="Type here..."
              defaultValue={additionalComments}
              onChange={(e) => {
                setAdditionalComments(e.target.value);
                teacherFeedback?.setAdditionalComments(e.target.value);
              }}
            />
          </div>
          <p className="text-xlarge w-full font-medium">Lesson Improvement</p>
          <p>How was the student's response towards:</p>
          <div className="inline-flex flex-col gap-3">
            <StarRatingForm
              name="Songs"
              initialRating={teacherFeedback?.teacherFeedback.responseToSongs}
              onChangeRating={(newRating) =>
                teacherFeedback?.setStudentResponseTo("SONGS", newRating)
              }
            />
            <StarRatingForm
              name="Activities"
              initialRating={
                teacherFeedback?.teacherFeedback.responseToActivities
              }
              onChangeRating={(newRating) =>
                teacherFeedback?.setStudentResponseTo("ACTIVITIES", newRating)
              }
            />
            <StarRatingForm
              name="Competition"
              initialRating={
                teacherFeedback?.teacherFeedback.responseToCompetition
              }
              onChangeRating={(newRating) =>
                teacherFeedback?.setStudentResponseTo("COMPETITION", newRating)
              }
            />
            <StarRatingForm
              name="Games"
              initialRating={teacherFeedback?.teacherFeedback.responseToGames}
              onChangeRating={(newRating) =>
                teacherFeedback?.setStudentResponseTo("GAMES", newRating)
              }
            />
            <StarRatingForm
              name="QnA"
              initialRating={teacherFeedback?.teacherFeedback.responseToQnA}
              onChangeRating={(newRating) =>
                teacherFeedback?.setStudentResponseTo("QNA", newRating)
              }
            />
          </div>
          <p>
            What skills out of the Bloom's Taxonomy were you able to cover in
            today's lesson?
          </p>
          <p className="text-brij-Grey">Select Multiple</p>
          <div className="flex flex-wrap gap-3">
            <div className="inline-flex gap-2 items-center">
              <Checkbox
                default={teacherFeedback?.teacherFeedback.bloomRemembering}
                onChange={(newVal) => {
                  teacherFeedback?.setBloomsStandard(
                    "bloomRemembering",
                    newVal
                  );
                }}
              />
              Remembering
            </div>
            <div className="inline-flex gap-2 items-center">
              <Checkbox
                default={teacherFeedback?.teacherFeedback.bloomAnalyzing}
                onChange={(newVal) => {
                  teacherFeedback?.setBloomsStandard("bloomAnalyzing", newVal);
                }}
              />
              Analyzing
            </div>
            <div className="inline-flex gap-2 items-center">
              <Checkbox
                default={teacherFeedback?.teacherFeedback.bloomApplying}
                onChange={(newVal) => {
                  teacherFeedback?.setBloomsStandard("bloomApplying", newVal);
                }}
              />
              Applying
            </div>
            <div className="inline-flex gap-2 items-center">
              <Checkbox
                default={teacherFeedback?.teacherFeedback.bloomUnderstanding}
                onChange={(newVal) => {
                  teacherFeedback?.setBloomsStandard(
                    "bloomUnderstanding",
                    newVal
                  );
                }}
              />
              Understanding
            </div>
            <div className="inline-flex gap-2 items-center">
              <Checkbox
                default={teacherFeedback?.teacherFeedback.bloomEvaluating}
                onChange={(newVal) => {
                  teacherFeedback?.setBloomsStandard("bloomEvaluating", newVal);
                }}
              />
              Evaluating
            </div>
            <div className="inline-flex gap-2 items-center">
              <Checkbox
                default={teacherFeedback?.teacherFeedback.bloomCreating}
                onChange={(newVal) => {
                  teacherFeedback?.setBloomsStandard("bloomCreating", newVal);
                }}
              />
              Creating
            </div>
          </div>
          <p>
            Are there any improvements you would like to suggest to improve the
            learning experience?
          </p>
          <div className="w-full">
            <textarea
              className="rounded-xl w-full"
              rows={3}
              onChange={(e) => {
                const newAdditionalComments = e.target.value;
                teacherFeedback?.setEngagementImprovementPlan(
                  newAdditionalComments
                );
              }}
              defaultValue={engagementImprovementPlan}
            />
          </div>
          <ButtonPrimary
            onClick={() => {
              toast.promise(submitFeedback(), {
                success: "Feedback Updated!",
                error: (e) => e.message,
                loading: "Updating Feedback...",
              });
            }}
          >
            Submit
          </ButtonPrimary>
        </div>
      </div>
    </div>
  );
}

interface SpecificStudentFeedbackProps {
  allStudents: Child[];
  teacherFeedback: TeacherFeedback;
}

function SpecificStudentFeedback(
  props: SpecificStudentFeedbackProps
): JSX.Element {
  const [curStudent, setCurStudent] = useState<Child>();
  const [studentFeedback, setStudentFeedback] = useState("");
  const [parentFeedback, setParentFeedback] = useState("");

  useEffect(() => {
    setCurStudent(props.allStudents[0]);
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!curStudent || !curStudent.childAccount.id) return;

      const curFeedback = await props.teacherFeedback.getChildTeacherFeedback(
        curStudent.childAccount.id
      );

      setStudentFeedback(curFeedback.feedbackForChild ?? "");
      setParentFeedback(curFeedback.feedbackForParent ?? "");
    };

    run();
  }, [curStudent, props.teacherFeedback]);

  //TODO: Debouncing
  const updateBackendStudentFeedback = async (newFeedback: string) => {
    if (!curStudent || !curStudent.childAccount.id) return;

    await props.teacherFeedback.setStudentFeedback(
      curStudent?.childAccount.id,
      newFeedback
    );
  };

  const updateBackendParentFeedback = async (newFeedback: string) => {
    if (!curStudent || !curStudent.childAccount.id) return;

    await props.teacherFeedback.setStudentParentFeedback(
      curStudent?.childAccount.id,
      newFeedback
    );
  };

  const updateStudentFeedback = (newVal: string) => {
    setStudentFeedback(newVal);
    updateBackendStudentFeedback(newVal);
  };

  const updateParentFeedback = (newVal: string) => {
    setParentFeedback(newVal);
    updateBackendParentFeedback(newVal);
  };

  //TODO: Render the feedback for the current student on first render
  return (
    <div className="p-6 inline-flex flex-col gap-2 border-2 rounded-xl">
      <div className="flex gap-2 w-full">
        <select
          className="rounded-xl h-[44px]"
          onChange={(e) => {
            const newStudent = new Child();
            newStudent.init(+e.target.value);
            setCurStudent(newStudent);
          }}
        >
          {props.allStudents.map((student) => {
            return (
              <option
                value={student.childAccount.id}
                key={student.childAccount.id}
              >
                {student.userDetail.firstName}
              </option>
            );
          })}
        </select>
        {curStudent && (
          <textarea
            className="rounded-xl w-full"
            rows={3}
            placeholder="Type here..."
            value={studentFeedback}
            onChange={(e) => updateStudentFeedback(e.target.value)}
          />
        )}
      </div>
      {curStudent && (
        <>
          <p>
            Do you have any recommendations for the parents of the selected
            child's learning?
          </p>

          <div className="w-full">
            <textarea
              className="rounded-xl w-full"
              rows={3}
              value={parentFeedback}
              placeholder="Type here..."
              onChange={(e) => updateParentFeedback(e.target.value)}
            />
          </div>
        </>
      )}
    </div>
  );
}

interface IStarRatingForm {
  name?: string;
  initialRating?: number | null;
  initialFeedback?: string;
  onChangeRating?: (newRating: number | undefined) => void;
  onChangeFeedback?: (newFeedback: string) => void;
}

export function StarRatingForm(props: IStarRatingForm) {
  const [rating, setRating] = useState<number | undefined>();
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    setRating(props.initialRating ?? undefined);
  }, [setRating, props.initialRating]);

  useEffect(() => {
    if (!props.initialFeedback) return;
    setFeedback(props.initialFeedback);
  }, [setFeedback, props.initialFeedback]);

  const changeRating = (newRating: number) => {
    let resolvedRating: number | undefined = newRating;
    if (newRating === rating) {
      resolvedRating = undefined;
    }
    setRating(resolvedRating);
    props.onChangeRating?.(resolvedRating);
  };

  const updateFeedback = async () => {
    setOpenFeedback(false);
  };

  return (
    <>
      <Modal open={openFeedback} onClose={() => setOpenFeedback(false)}>
        <BaseCard>
          <div className="inline-flex flex-col gap-3 w-full">
            <p>Feedback for this student</p>
            <div className="w-full">
              <textarea
                onChange={(e) => setFeedback(e.target.value)}
                className="rounded-xl w-full"
                rows={3}
                placeholder={feedback}
              />
            </div>
            <ButtonPrimary
              onClick={() => {
                toast.promise(updateFeedback(), {
                  success: "Feedback Updated!",
                  error: (e) => e.message,
                  loading: "Updating Feedback...",
                });
              }}
            >
              Update
            </ButtonPrimary>
          </div>
        </BaseCard>
      </Modal>
      <div className="flex w-full gap-4 justify-center">
        {props.name && (
          <>
            <p className="truncate w-[110px]">{props.name}</p>
            <p>:</p>
          </>
        )}
        <div className="inline-flex gap-1 items-center">
          <div
            onClick={() => changeRating(1)}
            className={`w-[24px] h-[24px] stroke-brij-Black cursor-pointer ${
              rating && rating >= 1 ? "fill-brij-Gold" : "fill-brij-Grey"
            }`}
          >
            <IconStar />
          </div>
          <div
            onClick={() => changeRating(2)}
            className={`w-[24px] h-[24px] stroke-brij-Black cursor-pointer ${
              rating && rating >= 2 ? "fill-brij-Gold" : "fill-brij-Grey"
            }`}
          >
            <IconStar />
          </div>
          <div
            onClick={() => changeRating(3)}
            className={`w-[24px] h-[24px] stroke-brij-Black cursor-pointer ${
              rating && rating >= 3 ? "fill-brij-Gold" : "fill-brij-Grey"
            }`}
          >
            <IconStar />
          </div>
          <div
            onClick={() => changeRating(4)}
            className={`w-[24px] h-[24px] stroke-brij-Black cursor-pointer ${
              rating && rating >= 4 ? "fill-brij-Gold" : "fill-brij-Grey"
            }`}
          >
            <IconStar />
          </div>
          <div
            onClick={() => changeRating(5)}
            className={`w-[24px] h-[24px] stroke-brij-Black cursor-pointer ${
              rating && rating >= 5 ? "fill-brij-Gold" : "fill-brij-Grey"
            }`}
          >
            <IconStar />
          </div>
        </div>
        {props.onChangeFeedback && (
          <div className="pl-2">
            <div
              className="w-[24px] h-[24px] fill-brij-Grey cursor-pointer"
              onClick={() => {
                setOpenFeedback(true);
              }}
            >
              <IconFeedback />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Input() {
  return (
    <p className="text-normal text-brij-DarkGrey left-6 top-4 inline leading-[1.5]">
      Type here...
    </p>
  );
}

function MCQButton(props: MCQButtonInterface) {
  return <>MCQbutton</>;
}

interface MCQButtonInterface {
  variant: "STATE=EMPTY" | "STATE=FILLED" | "STATE=CORRECT";
}
